import {Injectable, OnDestroy} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';
import {ShellClientService} from '../../shell-communication/shell-client.service';
import {MessageBusService} from '../../message-bus.service';
import {EventStreamConnectionChangedUIMessage} from '../../ui-messages/event-stream-connection-changed-message-ui-message.interface';
import {
    ANALYSIS_SESSION_CHANGED_MESSAGE_TYPE, ANALYSIS_SESSION_TOUCHED_MESSAGE_TYPE, AnalysisBlockDto, AnalysisBlockInput, AnalysisSessionChangedDto, AnalysisSessionDto, AnalysisSessionStatus,
    AnalysisSessionTouchedUIMessage, AssignAnalysisSessionHome, ExportAnalysisBlocks, GetAnalysisSession, ListPendingAnalysisBlocks, ListPendingAnalysisBlocksReply,
    ReplaceAnalysisChanges, ResetAnalysisSession, RevokeAnalysisBlocks, StartAnalysisSession
} from '../../shell-communication/analysis-session-operations';
import {PhaSnapshotItem} from './analysis-changes.builder';

// THE ANALYSIS SESSION, panel side (CR-20260930-001 M2). One instance PER PANEL (declared on the
// panel component's `providers`, never on the module — the Maven lesson): it holds the session the
// Shell handed back, talks to the nine session ops, and re-reads the session on every live push for
// it. The Shell is the authority on every status; this class never decides one. A refused COMMAND
// carries no text across the WCF boundary (M1 platform fact), so every refusal the trader can run
// into is asked about FIRST with a query (the orphan gate) and the rest is reported plainly.

/** How long the sheet may keep changing before its design is sent to the Shell (§5: "saved the moment it changes"). */
const REPLACE_SETTLE_MS = 600;

@Injectable()
export class AnalysisSessionService implements OnDestroy {

    constructor(
        private readonly _shellClient: ShellClientService,
        private readonly _messageBus: MessageBusService
    ) {
        this._messageBus
            .of<AnalysisSessionChangedDto>(ANALYSIS_SESSION_CHANGED_MESSAGE_TYPE)
            .pipe(takeUntil(this._unsub))
            .subscribe(msg => this.onPush(msg.payload));

        // The IN-APP notice (CR-20260930-001): the Collars panel publishes it the moment its own
        // Acquire / Clear command succeeded. The push can be lost to an ESS dropout; this cannot —
        // it never leaves the tab. The panel beside us re-reads at once.
        this._messageBus
            .of<AnalysisSessionTouchedUIMessage>(ANALYSIS_SESSION_TOUCHED_MESSAGE_TYPE)
            .pipe(takeUntil(this._unsub))
            .subscribe(msg => this.onTouched(msg.payload));

        // ESS is back: whatever was pushed while the socket was down may never be replayed (a
        // RESTORED session drops its unacked queue), so re-read the held session. With the slow
        // poll ruled out (owner), this re-read + the in-app notice are what bound the staleness.
        this._messageBus
            .of<EventStreamConnectionChangedUIMessage>('EventStreamConnectionChangedUIMessage')
            .pipe(takeUntil(this._unsub), filter(x => x.payload.isConnected))
            .subscribe(() => {
                if (this.session) {
                    console.info('[pha-session] ESS reconnected — re-reading the session');
                    this.reread();
                }
            });
    }

    private readonly _unsub = new Subject<void>();

    /** The live session as last read; null = no session (no combo picked, nothing added). */
    session: AnalysisSessionDto | null = null;

    private readonly _sessionChanged$ = new Subject<AnalysisSessionDto | null>();

    /** Fires after every read of the session (a push, an op) — the panel re-derives marks and counts. */
    get sessionChanged$(): Observable<AnalysisSessionDto | null> {
        return this._sessionChanged$;
    }

    private readonly _sessionEnded$ = new Subject<{sessionId: string; status: AnalysisSessionStatus | 'Ended'; reason: string}>();

    /** The session ended by a push from elsewhere (Finished / Aborted) or discovered gone on a read ('Ended'). */
    get sessionEnded$(): Observable<{sessionId: string; status: AnalysisSessionStatus | 'Ended'; reason: string}> {
        return this._sessionEnded$;
    }

    get sessionId(): string | null {
        return this.session ? this.session.sessionId : null;
    }

    get isLive(): boolean {
        return !!this.session && this.session.status === 'Live';
    }

    get isHomeless(): boolean {
        return !!this.session && !this.session.comboId;
    }

    get blocks(): AnalysisBlockDto[] {
        return this.session ? (this.session.blocks || []) : [];
    }

    private _snapshotCache: {json: string, items: PhaSnapshotItem[]} | null = null;

    /** The before snapshot the Shell took at start (camelCase on the wire; empty for a fresh start). Parsed once per session read. */
    get snapshot(): PhaSnapshotItem[] {
        if (!this.session || !this.session.beforeSnapshotJson) {
            return [];
        }
        if (this._snapshotCache && this._snapshotCache.json === this.session.beforeSnapshotJson) {
            return this._snapshotCache.items;
        }
        const items = this.parseSnapshot(this.session.beforeSnapshotJson);
        this._snapshotCache = {json: this.session.beforeSnapshotJson, items};
        return items;
    }

    private parseSnapshot(json: string): PhaSnapshotItem[] {
        try {
            const items = JSON.parse(json) as any[];
            return (items || []).map(i => ({
                tradingInstrumentCode: i.tradingInstrumentCode !== undefined ? i.tradingInstrumentCode : i.TradingInstrumentCode,
                kind: i.kind !== undefined ? i.kind : i.Kind,
                netPosition: i.netPosition !== undefined ? i.netPosition : i.NetPosition,
                avgPx: i.avgPx !== undefined ? i.avgPx : i.AvgPx,
                groupBucketId: i.groupBucketId !== undefined ? i.groupBucketId : (i.GroupBucketId || null),
                groupName: i.groupName !== undefined ? i.groupName : (i.GroupName || null)
            }));
        } catch (e) {
            console.error('[pha-session] before snapshot could not be read', e);
            return [];
        }
    }

    // ---- start / restore ----------------------------------------------------------------------

    private _selectionVersion = 0;

    /**
     * Import (§6.1): start a session on the combo or restore the live one; empty comboId = the
     * caller's homeless fresh start. Returns the session as the Shell holds it (null = refused).
     */
    async startOrRestore(comboId: string | null): Promise<AnalysisSessionDto | null> {
        const version = ++this._selectionVersion;
        try {
            await this._shellClient.processCommand(new StartAnalysisSession(comboId || ''));
        } catch (error) {
            console.error('[pha-session] StartAnalysisSession FAILED for combo ' + (comboId || '(homeless)'), error);
            if (version === this._selectionVersion) { this.setSession(null); }
            return null;
        }
        if (version !== this._selectionVersion) { return null; }
        return this.read(comboId, true);
    }

    /** Re-read the session of a combo (or the homeless one). */
    async read(comboId: string | null, throwOnFailure = false): Promise<AnalysisSessionDto | null> {
        const version = ++this._selectionVersion;
        let dto: AnalysisSessionDto | null = null;
        try {
            dto = await this._shellClient.processQuery<AnalysisSessionDto>(new GetAnalysisSession(comboId || ''));
        } catch (error) {
            console.error('[pha-session] GetAnalysisSession FAILED for combo ' + (comboId || '(homeless)'), error);
            if (throwOnFailure) { throw error; }
        }
        if (version !== this._selectionVersion) { return null; }
        this.setSession(dto || null);
        return this.session;
    }

    /** Re-read the current session by its combo (after an op, a push, a notice or a reconnect). */
    private async reread(): Promise<void> {
        if (!this.session) {
            return;
        }
        const comboId = this.session.comboId || null;
        const before = this.session.sessionId;
        const version = this._selectionVersion;
        let after: AnalysisSessionDto | null = null;
        try {
            after = await this._shellClient.processQuery<AnalysisSessionDto>(new GetAnalysisSession(comboId || ''));
        } catch (error) {
            // A FAILED read proves nothing: keep the held session and wait for the next trigger
            // (a notice, a push, a reconnect). Never end a session on a network hiccup.
            console.error('[pha-session] GetAnalysisSession FAILED for combo ' + (comboId || '(homeless)'), error);
            return;
        }
        if (version !== this._selectionVersion) { return; }
        this.setSession(after || null);
        if (!after || after.sessionId !== before) {
            // The held session is gone on a plain READ — no push was needed to learn it. Reads only
            // ever return LIVE sessions, so finish-vs-abort is not knowable here; 'Ended' tells the
            // panel the truth it needs: the design is dead and the combo is free (the re-import road).
            console.info('[pha-session] session ' + before + ' is no longer live on combo ' + (comboId || '(homeless)'));
            this._sessionEnded$.next({ sessionId: before, status: 'Ended', reason: 'not-live-on-read' });
        }
    }

    /** Forget the session (the panel moved to another combo). Nothing is sent. */
    detach(): void {
        ++this._selectionVersion;
        this.cancelPendingReplace();
        this.setSession(null);
    }

    private setSession(dto: AnalysisSessionDto | null): void {
        this.session = dto;
        this._sessionChanged$.next(dto);
    }

    // ---- design (ReplaceAnalysisChanges) -----------------------------------------------------

    private _replaceTimer: any = null;
    private _pendingReplace: {sheetStateJson: string, blocks: AnalysisBlockInput[]} | null = null;
    private _replaceInFlight = false;

    /**
     * The sheet changed: send its design after a short settle (typing, spinning, balance moves come
     * in bursts). Locked blocks must come back unchanged — the builder carries them through.
     */
    scheduleReplace(sheetStateJson: string, blocks: AnalysisBlockInput[]): void {
        if (!this.isLive) {
            return;
        }
        this._pendingReplace = {sheetStateJson, blocks};
        if (this._replaceTimer) {
            clearTimeout(this._replaceTimer);
        }
        this._replaceTimer = setTimeout(() => this.flushReplace(), REPLACE_SETTLE_MS);
    }

    /** Send the pending design now (before an export, so the Shell knows the block being copied). */
    async flushReplace(): Promise<boolean> {
        if (this._replaceTimer) {
            clearTimeout(this._replaceTimer);
            this._replaceTimer = null;
        }
        if (!this._pendingReplace || !this.isLive) {
            return true;
        }
        if (this._replaceInFlight) {
            // One at a time; the timer re-arms and the newest design goes out next.
            this._replaceTimer = setTimeout(() => this.flushReplace(), REPLACE_SETTLE_MS);
            return true;
        }
        const payload = this._pendingReplace;
        const sessionId = this.session.sessionId;
        const version = this._selectionVersion;
        this._pendingReplace = null;
        this._replaceInFlight = true;
        try {
            await this._shellClient.processCommand(new ReplaceAnalysisChanges(sessionId, payload.sheetStateJson, payload.blocks));
            // The Shell pushes "changes" for it; the re-read there brings the blocks with their group ids.
            return true;
        } catch (error) {
            console.error('[pha-session] ReplaceAnalysisChanges FAILED (' + payload.blocks.length + ' block(s))', error);
            // Keep the design pending so the next flush (before a copy) retries it and REPORTS the failure —
            // a copy must never proceed on a design the Shell does not hold (M2 smoke: it did, and the
            // export then found no such block).
            if (version === this._selectionVersion && this.sessionId === sessionId && !this._pendingReplace) {
                this._pendingReplace = payload;
            }
            return false;
        } finally {
            this._replaceInFlight = false;
        }
    }

    private cancelPendingReplace(): void {
        if (this._replaceTimer) {
            clearTimeout(this._replaceTimer);
            this._replaceTimer = null;
        }
        this._pendingReplace = null;
    }

    // ---- export / revoke / reset / home -------------------------------------------------------

    /**
     * The orphan gate (owner 2026-09-30), asked BEFORE the copy: a new session cannot export while
     * the combo carries blocks left behind by an aborted session. Returns how many, 0 = clear.
     */
    async countOrphansOnCombo(): Promise<number> {
        if (!this.session || !this.session.comboId) {
            return 0;
        }
        try {
            const reply = await this._shellClient.processQuery<ListPendingAnalysisBlocksReply>(new ListPendingAnalysisBlocks([this.session.comboId]));
            const combo = ((reply && reply.combos) || []).find(c => c.comboId === this.session.comboId);
            return combo ? combo.pendingBlocks.filter(b => b.isOrphaned).length : 0;
        } catch (error) {
            console.error('[pha-session] ListPendingAnalysisBlocks FAILED', error);
            return 0;
        }
    }

    /** Mark the blocks just copied as Exported, with the text the platform composed for each. */
    async export(blocks: {blockId: string, orderText: string}[]): Promise<boolean> {
        if (!this.isLive) {
            return false;
        }
        try {
            await this._shellClient.processCommand(new ExportAnalysisBlocks(this.session.sessionId, blocks));
            await this.reread();
            return true;
        } catch (error) {
            console.error('[pha-session] ExportAnalysisBlocks FAILED for ' + blocks.map(b => b.blockId).join(','), error);
            await this.reread();
            return false;
        }
    }

    /** Revoke at block / section / session scope: the block goes back to not-exported, its rows unlock. */
    async revoke(scope: 'block' | 'section' | 'session', blockId = '', section = ''): Promise<boolean> {
        if (!this.session) {
            return false;
        }
        try {
            await this._shellClient.processCommand(new RevokeAnalysisBlocks(this.session.sessionId, scope, blockId, section));
            await this.reread();
            return true;
        } catch (error) {
            console.error('[pha-session] RevokeAnalysisBlocks FAILED (' + scope + ' ' + (blockId || section) + ')', error);
            await this.reread();
            return false;
        }
    }

    /** Abort (§6.6). 'clear' removes the exported blocks from Collars too; 'leave' orphans them. */
    async reset(choice: 'clear' | 'leave'): Promise<boolean> {
        if (!this.isLive) {
            return false;
        }
        this.cancelPendingReplace();
        const id = this.session.sessionId;
        try {
            await this._shellClient.processCommand(new ResetAnalysisSession(id, choice));
            this.setSession(null);
            return true;
        } catch (error) {
            console.error('[pha-session] ResetAnalysisSession FAILED (' + choice + ') for ' + id, error);
            await this.reread();
            return false;
        }
    }

    /** Give a homeless session a home (§6.1): an existing portfolio + a new combo, or a new portfolio + combo. */
    async assignHome(terminalId: string, portfolioId: string | null, newPortfolioName: string, newComboName: string,
                     startingCapital: number): Promise<AnalysisSessionDto | null> {
        if (!this.isLive || !this.isHomeless) {
            return null;
        }
        const id = this.session.sessionId;
        try {
            await this._shellClient.processCommand(new AssignAnalysisSessionHome(id, terminalId, portfolioId || '', newPortfolioName || '', newComboName,
                startingCapital));
        } catch (error) {
            console.error('[pha-session] AssignAnalysisSessionHome FAILED for ' + id, error);
            return null;
        }
        // The session is now keyed by its new combo; the homeless read would come back empty.
        // The panel re-reads by combo once it knows the new combo id (from the portfolio list).
        return this.session;
    }

    // ---- the live push ------------------------------------------------------------------------

    /** The Collars panel's in-app notice: it just acquired / cleared blocks of OUR session — re-read. */
    private onTouched(touched: AnalysisSessionTouchedUIMessage): void {
        if (!this.session || touched.sessionId !== this.session.sessionId) {
            return;
        }
        console.info('[pha-session] in-app notice: ' + touched.reason + ' — re-reading the session');
        this.reread();
    }

    private onPush(dto: AnalysisSessionChangedDto): void {
        if (!dto || !this.session) {
            return;
        }
        const mine = dto.sessionId === this.session.sessionId
            || (!!dto.comboId && !!this.session.comboId && dto.comboId === this.session.comboId);
        if (!mine) {
            return;
        }
        console.info('[pha-session] push: ' + dto.reason + ' (' + (dto.blockIds || []).length + ' block(s)), status ' + dto.sessionStatus);
        // Every transition matters and the DTO carries only what changed: re-read the whole session.
        this.reread();
        // The session ENDED elsewhere (M3: "finished" once the last block was acquired in Portfolio
        // Hedging): tell the panel, which decides what next (it re-imports the combo on a finish).
        if (dto.sessionStatus && dto.sessionStatus !== 'Live') {
            this._sessionEnded$.next({ sessionId: dto.sessionId, status: dto.sessionStatus, reason: dto.reason });
        }
    }

    ngOnDestroy(): void {
        this.cancelPendingReplace();
        this._unsub.next();
        this._unsub.complete();
    }
}
